import { useState, useEffect } from 'react';
import { getRecentDailyCosts, getBudgetConfig } from '../../../services/firestore/opsService';
import type { DailyCostReport, BudgetConfig } from '../../../types/ops';

const C = {
  text: '#18181B', textSec: '#52525B', textMuted: '#A1A1AA',
  rose: '#E11D48', pass: '#059669', warn: '#B45309',
  bgCard: '#FFFFFF', border: '#E4E4E7',
};

export function CostsTab() {
  const [costs, setCosts] = useState<DailyCostReport[]>([]);
  const [budget, setBudget] = useState<BudgetConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [c, b] = await Promise.all([getRecentDailyCosts(7), getBudgetConfig()]);
        setCosts(c);
        setBudget(b);
      } catch (err) {
        console.error('Failed to load cost data:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: C.textMuted }}>Loading cost data...</div>;

  const weekTotal = costs.reduce((s, c) => s + c.total, 0);
  const avgDaily = costs.length > 0 ? weekTotal / costs.length : 0;
  const forecast30 = avgDaily * 30;
  const monthlyBudget = budget?.monthly?.total || 2000;

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
        <SummaryCard label="This Week" value={`$${weekTotal.toFixed(2)}`} />
        <SummaryCard label="Daily Average" value={`$${avgDaily.toFixed(2)}`} />
        <SummaryCard label="30-Day Forecast" value={`$${forecast30.toFixed(2)}`} color={forecast30 > monthlyBudget ? C.rose : C.pass} />
        <SummaryCard label="Monthly Budget" value={`$${monthlyBudget}`} />
      </div>

      {/* Provider breakdown */}
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20, marginBottom: 16 }}>
        <p style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: C.textMuted, marginBottom: 12 }}>
          Cost by Provider (Last 7 Days)
        </p>
        {costs.length === 0 ? (
          <p style={{ fontSize: 14, color: C.textSec }}>No cost data yet. Cost logging is active — data will appear after the first API calls.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Th>Date</Th>
                <Th style={{ textAlign: 'right' }}>Anthropic</Th>
                <Th style={{ textAlign: 'right' }}>Gemini</Th>
                <Th style={{ textAlign: 'right' }}>Firebase</Th>
                <Th style={{ textAlign: 'right' }}>Stripe</Th>
                <Th style={{ textAlign: 'right' }}>Total</Th>
                <Th>Budget</Th>
              </tr>
            </thead>
            <tbody>
              {costs.map((c) => {
                const dailyBudget = budget?.daily?.total || 100;
                const pct = Math.round((c.total / dailyBudget) * 100);
                const barColor = pct >= 90 ? C.rose : pct >= 75 ? C.warn : C.pass;
                return (
                  <tr key={c.date} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: '10px 12px', fontSize: 13, fontFamily: "'JetBrains Mono', monospace", color: C.textSec }}>{c.date}</td>
                    <td style={{ padding: '10px 12px', fontSize: 14, textAlign: 'right' }}>${c.byProvider.anthropic.toFixed(2)}</td>
                    <td style={{ padding: '10px 12px', fontSize: 14, textAlign: 'right' }}>${c.byProvider.gemini.toFixed(2)}</td>
                    <td style={{ padding: '10px 12px', fontSize: 14, textAlign: 'right' }}>${c.byProvider.firebase.toFixed(2)}</td>
                    <td style={{ padding: '10px 12px', fontSize: 14, textAlign: 'right' }}>${c.byProvider.stripe.toFixed(2)}</td>
                    <td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 600, textAlign: 'right' }}>${c.total.toFixed(2)}</td>
                    <td style={{ padding: '10px 12px', width: 120 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 6, background: '#F4F4F5', borderRadius: 3 }}>
                          <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: barColor, borderRadius: 3 }} />
                        </div>
                        <span style={{ fontSize: 12, color: barColor, fontWeight: 600, minWidth: 36 }}>{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20 }}>
      <p style={{ fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{label}</p>
      <p style={{ fontSize: 24, fontWeight: 700, color: color || C.text }}>{value}</p>
    </div>
  );
}

function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th style={{
      textAlign: 'left', fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: 0.5, color: '#A1A1AA', padding: '8px 12px', borderBottom: '1px solid #E4E4E7',
      ...style,
    }}>
      {children}
    </th>
  );
}
